import { useMemo, useState } from 'react';
import { Mail, X } from 'lucide-react';
import { useArchive } from '../hooks/useArchive';
import { useNewsletter } from '../hooks/useNewsletter';
import FeedCard from '../components/FeedCard';
import NewsletterModal from '../components/NewsletterModal';
import type { FeedItemDto } from '@shared/types';

const MAX_SELECTION = 5;
const MIN_SELECTION = 2;

export default function Archive() {
  const { data, isLoading, error } = useArchive();
  const newsletter = useNewsletter();
  const [selected, setSelected] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  const items = data ?? [];

  // Group by ISO date
  const byDate = useMemo(() => {
    const m = new Map<string, FeedItemDto[]>();
    for (const item of items) {
      const d = new Date(item.pickedAt * 1000);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const list = m.get(key) ?? [];
      list.push(item);
      m.set(key, list);
    }
    return m;
  }, [items]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_SELECTION) return prev;
      return [...prev, id];
    });
  };

  const clearSelection = () => setSelected([]);

  const generate = () => {
    setModalOpen(true);
    newsletter.mutate(selected);
  };

  const closeModal = () => {
    setModalOpen(false);
    newsletter.reset();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal/20 border-t-teal" />
      </div>
    );
  }

  if (error) {
    return <div className="card p-8 text-center text-danger">Chyba: {(error as Error).message}</div>;
  }

  if (items.length === 0) {
    return (
      <div>
        <h1 className="mb-2 font-heading text-2xl font-semibold text-dark">Archív</h1>
        <p className="mb-6 text-sm text-gray-600">Posledních 30 dní zpráv.</p>
        <div className="card p-8 text-center text-sm text-gray-500">
          Archív je zatím prázdný — buď ještě neproběhl žádný ingest, nebo všechny
          běhy starší než 30 dní.
        </div>
      </div>
    );
  }

  return (
    <div className="pb-24">
      <h1 className="mb-2 font-heading text-2xl font-semibold text-dark">Archív</h1>
      <p className="mb-6 text-sm text-gray-600">
        Posledních 30 dní · {items.length} {items.length === 1 ? 'zpráva' : items.length < 5 ? 'zprávy' : 'zpráv'}.
        U každé karty můžeš rozbalit „Vzor zprávy" stejně jako v dnešním feedu,
        nebo zaškrtnutím 2–5 zpráv vytvořit souhrnný newsletter (tlačítko se objeví dole).
      </p>

      <div className="space-y-8">
        {[...byDate.entries()].map(([date, dayItems]) => (
          <section key={date}>
            <h2 className="mb-3 font-heading text-lg font-semibold text-dark">
              {formatDay(dayItems[0].pickedAt)}
            </h2>
            <div className="space-y-4">
              {dayItems.map((item) => (
                <FeedCard
                  key={item.id}
                  item={item}
                  selectable
                  selected={selected.includes(item.id)}
                  selectDisabled={selected.length >= MAX_SELECTION}
                  onToggleSelected={() => toggleSelect(item.id)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      {selected.length > 0 && (
        <SelectionBar
          count={selected.length}
          canGenerate={selected.length >= MIN_SELECTION}
          onGenerate={generate}
          onClear={clearSelection}
        />
      )}

      {modalOpen && (
        <NewsletterModal
          itemCount={selected.length}
          loading={newsletter.isPending}
          error={newsletter.error as Error | null}
          data={newsletter.data?.data ?? null}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

function SelectionBar({
  count,
  canGenerate,
  onGenerate,
  onClear,
}: {
  count: number;
  canGenerate: boolean;
  onGenerate: () => void;
  onClear: () => void;
}) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-20 border-t border-gray-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
        <div className="text-sm text-gray-700">
          <strong className="text-dark">{count}</strong> / {MAX_SELECTION} vybráno
          {!canGenerate && (
            <span className="ml-2 text-xs text-gray-500">(min {MIN_SELECTION})</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onClear} className="btn-ghost text-sm">
            <X className="h-4 w-4" /> Zrušit výběr
          </button>
          <button
            onClick={onGenerate}
            disabled={!canGenerate}
            className="btn-primary"
          >
            <Mail className="h-4 w-4" /> Vytvořit newsletter
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDay(unixSec: number): string {
  const d = new Date(unixSec * 1000);
  const days = ['neděle', 'pondělí', 'úterý', 'středa', 'čtvrtek', 'pátek', 'sobota'];
  const months = ['ledna', 'února', 'března', 'dubna', 'května', 'června', 'července', 'srpna', 'září', 'října', 'listopadu', 'prosince'];
  return `${days[d.getDay()]}, ${d.getDate()}. ${months[d.getMonth()]}`;
}
